import {
	Badge,
	Button,
	DefaultImageProps,
	DefaultLinkProps,
	Heading,
	Icon,
	Image,
	ImageProps,
	Link,
	LinkProps,
	RichText,
	Text
} from "@pamperedchef/design-system";
import { className } from "@/app/utilities/className";
import type { HeadingTag, SeasonTheme } from "@/app/utilities/types";

import * as styles from "./EvergreenOfferingCard.module.scss";

const BACKGROUND_ICON_NAMES: Record<string, string> = {
	bubble: "bubble-background",
	stripe: "stripe-background",
	line: "wave-background"
};

export interface EvergreenOfferingCardProps<
	ImageComponentProps = DefaultImageProps,
	LinkComponentProps = DefaultLinkProps
> {
	eyebrow?: string;
	headline?: string;
	headlineTag?: HeadingTag;
	subheading?: string;
	body?: string;
	promoText?: string;
	promoIcon?: string;
	image: ImageProps<ImageComponentProps>;
	primaryCta?: LinkProps<LinkComponentProps>;
	secondaryCta?: LinkProps<LinkComponentProps>;
	theme?: SeasonTheme;
	backgroundShape?: "bubble" | "stripe" | "line";
}

/**
 * Evergreen Offering Card Component
 * @returns JSX.Element
 */
export function EvergreenOfferingCard<
	ImageComponentProps = DefaultImageProps,
	LinkComponentProps = DefaultLinkProps
>({
	eyebrow,
	headline,
	headlineTag = "h3",
	subheading,
	body,
	promoText,
	promoIcon = "GiftOpen",
	image,
	primaryCta,
	secondaryCta,
	theme = "standard",
	backgroundShape = "bubble"
}: EvergreenOfferingCardProps<
	ImageComponentProps,
	LinkComponentProps
>): JSX.Element {
	const backgroundIconName = BACKGROUND_ICON_NAMES[backgroundShape];

	return (
		<div className={styles["evergreen-offering-card"]}>
			{image ? (
				<div className={styles["evergreen-offering-card__asset"]}>
					<Image
						{...(image as any)}
						alt={image.alt || "Evergreen Offering Image"}
						aspectRatio="16:9"
					/>
				</div>
			) : null}

			<div className={styles["evergreen-offering-card__body"]}>
				<Text
					className={styles["evergreen-offering-card__eyebrow"]}
					styledAs="xsb"
				>
					{eyebrow}
				</Text>

				<Heading styledAs="h2" tag={headlineTag}>
					{headline}
				</Heading>

				<Text
					className={styles["evergreen-offering-card__legend"]}
					styledAs="xsr"
				>
					{subheading}
				</Text>

				<RichText
					className={styles["evergreen-offering-card__copy"]}
					content={body || ""}
				/>

				<Badge
					kind="default"
					className={styles["evergreen-offering-card__badge"]}
					icon={{
						name: promoIcon as string,
						className: styles["evergreen-offering-card__promo-icon"]
					}}
					label={promoText || ""}
				/>
			</div>

			<div
				{...className(
					styles["evergreen-offering-card__actions"],
					theme &&
						styles[`evergreen-offering-card__actions--${theme}`]
				)}
			>
				<div
					className={styles["evergreen-offering-card__actions__cta"]}
				>
					{primaryCta?.href ? (
						<Button
							kind="secondary"
							size="lg"
							onPress={() => {
								const href = (primaryCta as any).href;
								const target = (primaryCta as any).target;
								if (target === "_blank") {
									window.open(href, "_blank");
								} else {
									window.location.href = href;
								}
							}}
						>
							{primaryCta.children}
						</Button>
					) : null}
					{secondaryCta?.href ? (
						<Link kind="tertiary" {...secondaryCta} />
					) : null}
				</div>

				<Icon
					name={backgroundIconName}
					className={styles["evergreen-offering-card__background"]}
				/>
			</div>
		</div>
	);
}
